import { getCompatibility, getTechSpec } from "@/libs/tactsuit/getTechSpecs";
import React, { Fragment } from "react";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { ProductSlug } from "@/api/product-api";
import TactGloveSizing from "@/v2-components/size-guide-sheet/TactGloveSizing";

const TechSpecItem = ({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className={"flex flex-col items-start text-lg laptop:flex-row"}>
    <div
      className={cn(
        "flex flex-row items-center text-base font-600 laptop:text-xl",
        "w-full border-b py-5 laptop:w-[342px] laptop:border-0 laptop:py-0",
      )}
    >
      {label}
    </div>
    <div className="mb-4 flex w-full flex-col items-center justify-center gap-2 py-9 laptop:mb-0 laptop:items-start laptop:pb-10 laptop:pt-0">
      {children}
    </div>
  </div>
);

const TechSpecHeader = ({ children }: { children: React.ReactNode }) => (
  <div className={"text-center text-6xl laptop:text-left laptop:text-7xl"}>
    {children}
  </div>
);

const TechSpecsDetail = ({ slug }: { slug: string }) => {
  const specs = getTechSpec(slug);
  const { t } = useTrans(["tactsuit"]);

  return (
    <div className={"my-16"}>
      <TechSpecItem
        label={
          <div className={"laptop:w-[150px]"}>
            {t("Number of Feedback Points")}
          </div>
        }
      >
        <TechSpecHeader>{specs.hapticPoints.number}</TechSpecHeader>
        <div className="text-base">{t(specs.hapticPoints.description)}</div>
      </TechSpecItem>

      <TechSpecItem label={t("Playtime")}>
        <TechSpecHeader>{`${specs.playtime.hours} Hrs`}</TechSpecHeader>
        <div className="text-center text-base text-coolgray-60">
          {t(specs.playtime.note)}
        </div>
      </TechSpecItem>

      <TechSpecItem label={t("Size Details")}>
        {slug === ProductSlug.TACTGLOVE_DK2 ? (
          <TactGloveSizing />
        ) : (
          <Fragment>
            <TechSpecHeader>{t(specs.sizeDetails.type)}</TechSpecHeader>
            <div className="text-base laptop:text-lg">
              {t(specs.sizeDetails.size)}
            </div>
            <div className="text-base laptop:text-lg">
              {t(specs.sizeDetails.weight)}
            </div>
            <div className="whitespace-pre-line text-base laptop:text-lg">
              {t(specs.sizeDetails.note)}
            </div>
          </Fragment>
        )}
      </TechSpecItem>

      <TechSpecItem
        label={
          <div className={"laptop:w-[150px]"}>{t("Charging Time Battery")}</div>
        }
      >
        <TechSpecHeader>{specs.chargingAndBattery.chargingTime}</TechSpecHeader>
        <div className="text-base laptop:text-lg">
          {t(specs.chargingAndBattery.chargeDetails)}
        </div>
        <div className="text-base text-coolgray-60">
          {specs.chargingAndBattery.batterySpec}
        </div>
      </TechSpecItem>

      <TechSpecItem
        label={
          <>
            <span>{t("Connectivity")}</span>
          </>
        }
      >
        <TechSpecHeader>{t(specs.connectivity.type)}</TechSpecHeader>
        <div className="text-base laptop:text-lg">
          {t(specs.connectivity.description)}
        </div>
        <div className="text-center text-base text-coolgray-60 laptop:text-start">
          {t(specs.connectivity.note)}
        </div>
      </TechSpecItem>

      <TechSpecItem label={t("Compatibility")}>
        <div className="flex flex-col gap-4">
          {getCompatibility(slug).map((c, index) => {
            return (
              <div
                key={index}
                className="flex flex-col gap-y-1 whitespace-pre-wrap text-center text-base laptop:flex-row laptop:text-start laptop:text-lg"
              >
                <span className={"hidden font-600 laptop:block"}>
                  {`${c.platform}: `}
                </span>
                <span className={"font-600 laptop:hidden"}>{c.platform}</span>
                <span>{c.value}</span>
              </div>
            );
          })}
        </div>
      </TechSpecItem>
    </div>
  );
};

export default TechSpecsDetail;
